import React, { Component, useState, useEffect, Fragment } from "react";
import moment from "moment";
import AuthenticatedPage from "../../../containers/AuthenticatedPage";
import {
  Checkbox,
  MultiSelect,
  SingleDatePicker,
} from "../../../components/Form";
import Select from "../../../components/Form/Inputs/Select";
import { Dialog, Popup } from "../../../components/Popup";
import Loader from "../../../components/Loader";
import Tabs from "../../../components/Tabs";

import API from "../../../lib/api";
import { getMessage } from "../../../lib/translator";
import { getStores } from "../../../lib/auth";
import ReportsDownload from "../reports-downloads";

import "./style.css";
import {
  downloadCSV,
  getRenderParams,
  isValid,
  create180DaysTimeSpanHandler,
} from "./helpers";

const reportEndpoint = "/report-service/reports";

const dateRangeOptions = ["Day", "Week", "Month", "Custom"];

const getStoreOptionsSortedByClientId = () => {
  return getStores()
    ?.filter((store) => store.hasPicking || store.hasClickCollect)
    .sort((storeA, storeB) => {
      const numA = storeA.clientId?.match(/\d+/);
      const numB = storeB.clientId?.match(/\d+/);

      const numericA = numA ? parseInt(numA[0]) : null;
      const numericB = numB ? parseInt(numB[0]) : null;

      // Sort: numeric names first, others later
      if (numericA === null && numericB === null) return 0;
      if (numericA === null) return 1;
      if (numericB === null) return -1;

      return numericA - numericB;
    })
    .map((store) => ({
      text: store.name,
      value: store.id,
    }));
};

const getUnBoundRestriction = (values) => {
  for (let i in values) {
    if (values[i].unbounded) return true;
  }
  return false;
};

const DownloadReportForm = ({ handleClose, selectedReport, _this }) => {
  const [data, setData] = useState({});
  const [dateRangeType, setDateRangeType] = useState("Month");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const storeOptions = getStoreOptionsSortedByClientId();

  const handleDateRangeTypeChange = (e) => {
    setDateRangeType(e.target.value);
  };

  const handleSubmit = (key) => {
    setIsProcessing(true);
    const reportId = selectedReport?.id;
    const postReports = [
      "Delivery Agent Report",
      "Item Count Report",
      "Payment Raw Data Report",
      "Item Sales Report",
      "Order Sales Report",
      "Barcode and Keyword Report",
      "DFR Report",
      "Picker Item Drop",
      "Brands Report",
      "Categories Report",
      "Fill Rate Report",
      "B2B Sales Report",
      "Inventory Transaction Report",
      "Picking Summary",
      "Individual Picker Performance",
      "Out Of Stock Products Report",
      "Product Returns Report",
      "Order Sales Reports",
      "Edited Items Report",
      "Coupon Report",
      "Product Sales Report",
    ];
    const entityMapping = {
      "Delivery Agent Report": "delivery-agent-report",
      "Item Count Report": "item-count-report",
      "Payment Raw Data Report": "payment-raw-data-report",
      "Item Sales Report": "item-sales-report",
      "Order Sales Report": "order-sales-report-v2",
      "Barcode and Keyword Report": "keyword-barcode-search-report",
      "DFR Report": "dfr-report",
      "Picker Item Drop": "picker-item-drop-report",
      "Brands Report": "brand-report",
      "Categories Report": "category-report",
      "Fill Rate Report": "fill-rate-report",
      "B2B Sales Report": "b2b-sales-report",
      "Inventory Transaction Report": "inventory-report",
      "Picking Summary": "picking-summary-report",
      "Individual Picker Performance": "picker-performance-report",
      "Out Of Stock Products Report": "out-of-stock-products-report",
      "Product Returns Report": "product-return-report",
      "Order Sales Reports": "order-sales-report",
      "Edited Items Report": "edited-items-report-v2",
      "Coupon Report": "coupon-report",
      "Product Sales Report": "product-sales-report",
    };
    const dateOnlyReports = [
      "DFR Report",
      "Picker Item Drop",
      "Fill Rate Report",
      "Picking Summary",
    ];

    const entityType = entityMapping?.[selectedReport?.name];
    const isDateOnlyReport = dateOnlyReports.includes(selectedReport?.name);

    let params = {
      entity: entityType,
      type: "DOWNLOAD",
      ...(isDateOnlyReport
        ? { date: data.date }
        : { startDate: data.startDate, endDate: data.endDate }),
      ...(storeConfig?.key && data?.[storeConfig?.key]
        ? { storeIds: [Number(data?.[storeConfig?.key])] }
        : {}),
      ...(multiStoreConfig?.key &&
      Array.isArray(data?.[multiStoreConfig?.key]) &&
      data?.[multiStoreConfig?.key].length
        ? { storeIds: data?.[multiStoreConfig?.key] }
        : {}),
    };

    const isBulkProcessing = postReports.includes(selectedReport?.name);

    const url = isBulkProcessing
      ? "/bulk-processing-service/file-request"
      : `${reportEndpoint}/${reportId}`;
    if (!isBulkProcessing) {
      params = Object.keys(data).reduce((acc, curr) => {
        if (Array.isArray(data[curr])) {
          acc[curr] = data[curr].join(",");
        } else {
          acc[curr] = encodeURIComponent(data[curr]);
          if (curr === "startDate") {
            acc[curr] += " 00:00:00";
          } else if (curr === "endDate") {
            acc[curr] += " 23:59:59";
          }
        }
        return acc;
      }, {});
      params.requestType = key;
      if (selectedReport.name === "Customers Report") {
        params.includeOrder = true;
      }
    }
    const api = new API({ url });
    const request = isBulkProcessing ? api.post(params) : api.get(params);
    request
      .then((resp) => {
        handleClose();
        if (key === "download") {
          downloadCSV(resp.data, selectedReport?.name);
        }
        if (key === "email") {
          const successMessage =
            typeof resp.data !== "object"
              ? resp.data
              : "Your report is on its way! You'll receive it via email shortly.";
          _this.setState({
            formError: "",
            successMessage: successMessage,
            showSuccessDialog: true,
          });
        }
      })
      .catch((error) => {
        const errorMsg = error?.message || "Something went wrong.";
        handleClose();
        _this.setState({
          showSuccessDialog: false,
          showErrorDialog: true,
          formError: errorMsg,
        });
      })

      .finally(() => {
        setIsProcessing(false);
      });
  };

  const createDataChangeHandler = (key) => (date) => {
    const newData = { ...data, [key]: date };
    if (multiStoreConfig?.key === key) {
      if (date?.length !== storeOptions?.length) {
        setSelectAll(false);
      } else {
        setSelectAll(true);
      }
    }
    setData(newData);
  };

  /* Recalculate the date-range  whenever different timespan is selected*/
  useEffect(() => {
    const { startDateConfig, endDateConfig } = getRenderParams(
      selectedReport?.params
    );

    if (startDateConfig && endDateConfig) {
      if (dateRangeType !== "Custom") {
        let numOfDays = 30;
        if (dateRangeType === "Day") {
          numOfDays = 1;
        } else if (dateRangeType === "Week") {
          numOfDays = 7;
        }

        const newDates = {
          startDate: moment().subtract(numOfDays, "d").format("YYYY-MM-DD"),
          endDate: moment().format("YYYY-MM-DD"),
        };

        setData((data) => ({ ...data, ...newDates }));
      }
    }
  }, [dateRangeType, selectedReport]);

  // Reset endDate is selected span is more than 180 days
  useEffect(() => {
    const { params } = selectedReport;
    const isBounded = getUnBoundRestriction(params);

    if (data.startDate && data.endDate && !isBounded) {
      if (moment(data.startDate).diff(data.endDate, "day") < -180) {
        setData((data) => ({ ...data, endDate: undefined }));
      }
    }
  }, [data.startDate, data.endDate, selectedReport]);

  const {
    storeConfig,
    startDateConfig,
    endDateConfig,
    otherDateConfigs,
    multiStoreConfig,
  } = getRenderParams(selectedReport?.params);

  const canbeSubmitted = !isProcessing && isValid(data, selectedReport?.params);

  const handleSelectAll = (key, value) => {
    let storeIds;
    if (value) {
      storeIds = storeOptions?.map((store) => store.value);
    } else {
      storeIds = [];
    }
    const newData = { ...data, [key]: storeIds };
    setData(newData);
    setSelectAll(value);
  };

  return (
    <Popup
      className="report-form"
      heading={getMessage("analytics.report.modal.title")}
      show
      close={handleClose}
    >
      {storeConfig && (
        <div>
          <label htmlFor="store" className="report-field-label">
            {getMessage("store.store.label")}
          </label>
          <Select
            placeholder={"Select store"}
            name={storeConfig.key}
            options={storeOptions}
            value={data[storeConfig.key] ? Number(data[storeConfig.key]) : ""}
            onChange={createDataChangeHandler(storeConfig.key)}
          />
        </div>
      )}
      {multiStoreConfig && (
        <MultiSelect
          className="product-type stores"
          label={getMessage("product.selectStore")}
          placeholder={getMessage("coupon.form.stores.placeholder")}
          name={multiStoreConfig.key}
          options={storeOptions}
          value={data[multiStoreConfig.key]}
          onChange={createDataChangeHandler(multiStoreConfig.key)}
          secondaryLabel={() => (
            <Checkbox
              inlineLabel={getMessage("select all")}
              name="selectAllStores"
              value={selectAll}
              onChange={(value) => handleSelectAll(multiStoreConfig.key, value)}
              className="checkLabel"
              key={selectAll}
            />
          )}
        />
      )}
      {startDateConfig && endDateConfig && (
        <div>
          <span className="report-field-label">
            {getMessage("analytics.report.dateRange")}
          </span>
          <div className="date-range-radio">
            {dateRangeOptions.map((val) => (
              <div key={`radio-${val}`}>
                <input
                  type="radio"
                  name="dateRangeType"
                  id={`radio-${val}`}
                  value={val}
                  checked={val === dateRangeType}
                  onChange={handleDateRangeTypeChange}
                />
                <label htmlFor={`radio-${val}`}>{val}</label>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="date-selector">
        {startDateConfig && (
          <SingleDatePicker
            openDirection="down"
            className="date-range-field"
            enableToday
            isOutsideRange={(day) => moment().diff(day) < 0}
            label={startDateConfig.name}
            onChange={createDataChangeHandler(startDateConfig.key)}
            displayFormat={() => "MMM D YYYY"}
            value={data[startDateConfig.key]}
            disabled={dateRangeType !== "Custom"}
          />
        )}
        {startDateConfig && endDateConfig && (
          <span className="date-range-separator">-</span>
        )}
        {endDateConfig && (
          <SingleDatePicker
            openDirection="down"
            className="date-range-field"
            isOutsideRange={create180DaysTimeSpanHandler(data.startDate)}
            customIsOutsideRange
            label={endDateConfig.name}
            onChange={createDataChangeHandler(endDateConfig.key)}
            displayFormat={() => "MMM D YYYY"}
            value={data[endDateConfig.key]}
            disabled={dateRangeType !== "Custom"}
          />
        )}
        {otherDateConfigs &&
          otherDateConfigs.map((param, index) => (
            <SingleDatePicker
              openDirection="down"
              enableToday
              isOutsideRange={(day) => moment().diff(day) < 0}
              label={param.name}
              onChange={createDataChangeHandler(param.key)}
              displayFormat={() => "MMM D YYYY"}
              value={data[param.key]}
              key={"other-date-" + index}
            />
          ))}
      </div>
      <div className="button-action">
        {selectedReport?.requestTypes
          ?.filter((requestType) => requestType?.name === "email")
          ?.map((requestTypes) => (
            <button
              className={requestTypes?.name + "-report-btn"}
              disabled={!canbeSubmitted}
              onClick={() => handleSubmit(requestTypes?.name)}
            >
              {getMessage(
                isProcessing
                  ? "analytics.report.processing"
                  : requestTypes?.name === "download"
                  ? "analytics.report.modal.title"
                  : "analytics.report.send.email"
              )}
            </button>
          ))}
      </div>
    </Popup>
  );
};

class AnalyticsReport extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  fetchReports = () => {
    this.setState({ loading: true });
    const api = new API({ url: reportEndpoint });

    api
      .get()
      .then((reports) => {
        const modifiedResp = Object.keys(reports.data).reduce((acc, curr) => {
          return [...acc, { ...reports.data[curr], id: curr }];
        }, []);
        this.setState({ reports: modifiedResp, loading: false });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  handleShowModal = (report) => {
    this.setState({ showModal: true, selectedReport: report });
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  closeDialogs = () => {
    this.setState({
      showSuccessDialog: false,
      showErrorDialog: false,
    });
  };

  componentDidMount() {
    this.fetchReports();
  }

  render() {
    const {
      reports,
      showModal,
      selectedReport,
      loading,
      showSuccessDialog,
      showErrorDialog,
      formError,
      successMessage,
    } = this.state;

    return (
      <>
        <h1 className="title">{getMessage("analytics.reports.heading")}</h1>
        {loading ? (
          <Loader />
        ) : (
          <Fragment>
            {showModal && (
              <DownloadReportForm
                handleClose={this.closeModal}
                selectedReport={selectedReport}
                _this={this}
              />
            )}
            <h3 className="section-heading">
              {getMessage("analytics.report.recent")}
            </h3>
            <div className="reports-card-conatiner">
              {reports?.map((report, index) => (
                <div
                  className="report-card"
                  key={report.name + index}
                  onClick={() => this.handleShowModal(report)}
                >
                  <span className="placeholder" />
                  <span>{report.name}</span>
                </div>
              ))}
            </div>
            <Dialog
              show={showSuccessDialog}
              title={getMessage("success.text.title")}
              className="success"
              information={successMessage}
              close={this.closeDialogs}
              closeText={getMessage("okay.text")}
            />
            <Dialog
              show={showErrorDialog}
              title={getMessage("error.generic")}
              information={formError}
              close={this.closeDialogs}
              closeText={getMessage("okay.text")}
            />
          </Fragment>
        )}
      </>
    );
  }
}

class AnalyticsReportWrapper extends Component {
  constructor() {
    super();
    this.state = {
      activeIndex: 0,
    };
  }

  changeTab = (index) => {
    this.setState({ activeIndex: index });
  };

  render() {
    const { activeIndex } = this.state;

    return (
      <AuthenticatedPage
        menu={this.props.menu}
        className={
          activeIndex === 0 ? "analytics-report-page" : "reports-download-page"
        }
      >
        <div className="tab-wraps">
          <Tabs
            items={["Reports", "ReportsDownload"].map((tab) =>
              getMessage(`analytics.report.title.${tab.toLowerCase()}.tab`)
            )}
            default={Number(this.state.activeIndex)}
            onClick={(index) => this.changeTab(index)}
          />
        </div>

        {activeIndex === 0 ? (
          <AnalyticsReport {...this.props} />
        ) : (
          <ReportsDownload {...this.props} />
        )}
      </AuthenticatedPage>
    );
  }
}

export default AnalyticsReportWrapper;
